// packages/web/src/lib/url-utils.ts
// URL utility functions for cross-domain embedding support

/**
 * Get the platform domain from environment variable or detect from context
 * Supports both dev (visionarynow.com) and prod (aithon.tech)
 */
export function getPlatformDomain(): string {
  // Check environment variable first
  if (process.env.NEXT_PUBLIC_PLATFORM_DOMAIN) {
    return process.env.NEXT_PUBLIC_PLATFORM_DOMAIN;
  }

  if (typeof window !== 'undefined') {
    // Client-side: use current hostname for automatic detection
    return window.location.hostname;
  }

  // Server-side: use NODE_ENV detection
  return process.env.NODE_ENV === 'production' ? 'aithon.tech' : 'visionarynow.com';
}

/**
 * Get the full platform URL for a given path
 * @param relativeUrl - The relative URL (e.g., '/assets/brands/default/logo.png')
 * @returns Absolute URL (e.g., 'https://aithon.tech/assets/brands/default/logo.png')
 */
export function getPlatformUrl(relativeUrl: string = '/'): string {
  const domain = getPlatformDomain();
  const path = relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`;
  return `https://${domain}${path}`;
}

/**
 * Convert a relative URL to an absolute URL, but ONLY when an absolute
 * URL is actually required (cross-domain embedding / iframe scenarios).
 * For same-origin rendering we return the relative path unchanged so
 * that the browser resolves against window.location.origin -- which is
 * always correct on both visionarynow.com (dev) and aithon.tech (prod)
 * without any env wiring.
 *
 * The old implementation hard-coded `aithon.tech` whenever
 * `process.env.NODE_ENV === 'production'` was true during SSR, which
 * meant dev-host SSR (built with `next build`) emitted
 * `https://aithon.tech/assets/...` img srcs. Those 404 on dev and served
 * mixed-origin cookies on prod. Since the asset is same-origin in every
 * real deployment, the right default is 'leave it relative'.
 *
 * Absolute conversion still happens when NEXT_PUBLIC_PLATFORM_DOMAIN is
 * explicitly set, so the embed / iframe use case remains supported.
 *
 * @param relativeUrl - The relative URL (e.g., '/assets/brands/default/logo.png')
 * @returns Either the original relative URL (default) or an absolute
 *   URL when NEXT_PUBLIC_PLATFORM_DOMAIN is configured.
 */
export function toAbsoluteUrl(relativeUrl: string | null | undefined): string {
  if (!relativeUrl) return '';

  // Already absolute
  if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
    return relativeUrl;
  }

  // Data URLs: return as-is.
  if (relativeUrl.startsWith('data:')) {
    return relativeUrl;
  }

  // Protocol-relative URL: honour scheme.
  if (relativeUrl.startsWith('//')) {
    return `https:${relativeUrl}`;
  }

  // Ensure leading slash for relative paths.
  const path = relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`;

  // Same-origin default: return relative. Browsers resolve against the
  // current origin, so visionarynow.com gets /assets/..., aithon.tech
  // gets /assets/..., both are correct.
  const explicitDomain = process.env.NEXT_PUBLIC_PLATFORM_DOMAIN;
  if (explicitDomain) {
    return `https://${explicitDomain}${path}`;
  }
  return path;
}

/**
 * Generate a complete base tag HTML for embedding
 *
 * @returns HTML string for the base tag
 */
export function getBaseTagHtml(): string {
  const domain = getPlatformDomain();
  return `<base href="https://${domain}/">`;
}

// Export for convenience
export const PLATFORM_DOMAIN = getPlatformDomain();
